import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import Login from './components/Login';
import Register from './components/Register';
import Dashboard from './components/Dashboard';
import UserProfile from './components/UserProfile';
import PasswordReset from './components/PasswordReset';
import EmailVerification from './components/EmailVerification';
import RegistrationComplete from './components/RegistrationComplete';
import TermsOfService from './components/TermsOfService';
import Rules from './components/Rules';
import Blog from './components/Blog';
import Health from './components/Health';

// Custom hook to handle browser back button within the SPA
const useBackButtonHandler = (isAuthenticated) => {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handlePopState = (event) => {
      // If user is authenticated and trying to go back to login/register pages,
      // redirect them to dashboard instead
      if (isAuthenticated && (location.pathname === '/login' || location.pathname === '/register')) {
        event.preventDefault();
        navigate('/dashboard', { replace: true });
        return;
      }
      
      // If user is not authenticated and trying to access protected routes,
      // redirect to login
      if (!isAuthenticated && location.pathname !== '/login' && location.pathname !== '/register' && 
          location.pathname !== '/verify-email' && location.pathname !== '/reset-password' && 
          location.pathname !== '/registration-complete' && location.pathname !== '/terms-of-service' && 
          location.pathname !== '/rules' && location.pathname !== '/health') {
        navigate('/login', { replace: true });
        return;
      }
    };

    // Add event listener for popstate (back/forward button)
    window.addEventListener('popstate', handlePopState);
    
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [isAuthenticated, location.pathname, navigate]);
};

// Component to handle back button logic
const BackButtonHandler = ({ isAuthenticated }) => {
  useBackButtonHandler(isAuthenticated);
  return null;
};

// Wrapper component that can use React Router hooks
const AppWithBackButtonHandler = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);

  const checkAuthStatus = () => {
    const token = localStorage.getItem('token');
    setIsAuthenticated(!!token);
    setLoading(false);
  };

  useEffect(() => {
    checkAuthStatus();
    
    // Listen for storage changes (when token is removed during logout)
    const handleStorageChange = (e) => {
      if (e.key === 'token') {
        checkAuthStatus();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    
    // Also listen for custom logout events
    const handleLogout = () => {
      checkAuthStatus();
    };

    // Listen for custom login events
    const handleLogin = () => {
      checkAuthStatus();
    };

    window.addEventListener('logout', handleLogout);
    window.addEventListener('login', handleLogin);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('logout', handleLogout);
      window.removeEventListener('login', handleLogin);
    };
  }, [isAuthenticated]);

  const PrivateRoute = ({ children }) => {
    if (loading) {
      return <div>Loading...</div>;
    }
    return isAuthenticated ? children : <Navigate to="/login" />;
  };

  const PublicRoute = ({ children }) => {
    if (loading) {
      return <div>Loading...</div>;
    }
    return !isAuthenticated ? children : <Navigate to="/dashboard" />;
  };

  return (
    <Router>
      <BackButtonHandler isAuthenticated={isAuthenticated} />
      <Routes>
        <Route 
          path="/login" 
          element={
            <PublicRoute>
              <Login />
            </PublicRoute>
          } 
        />
        <Route 
          path="/register" 
          element={
            <PublicRoute>
              <Register />
            </PublicRoute>
          } 
        />
        <Route 
          path="/verify-email" 
          element={<EmailVerification />} 
        />
        <Route 
          path="/reset-password" 
          element={<PasswordReset />} 
        />
        <Route 
          path="/registration-complete" 
          element={<RegistrationComplete />} 
        />
        <Route 
          path="/dashboard" 
          element={
            <PrivateRoute>
              <Dashboard />
            </PrivateRoute>
          } 
        />
        <Route 
          path="/users/:userId" 
          element={
            <PrivateRoute>
              <UserProfile />
            </PrivateRoute>
          } 
        />
        <Route path="/terms-of-service" element={<TermsOfService />} />
        <Route path="/rules" element={<Rules />} />
        <Route path="/blog" element={<Blog />} />
        <Route path="/health" element={<Health />} />
        <Route path="/" element={<Navigate to="/login" />} />
      </Routes>
    </Router>
  );
};

export default AppWithBackButtonHandler; 